import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  KeyRound,
  Radio,
  Trophy,
  UserCog,
  Users,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { DatabinexScene } from '../components/visuals/DatabinexScene';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { errorMessage } from '../lib/callables';
import { formatFaNumber } from '../lib/format';
import { useAuth } from '../providers/AuthProvider';

type LoginMode = 'team' | 'admin';

export function LoginPage() {
  const { loginWithTeamCode, loginAsAdmin } = useAuth();
  const [mode, setMode] = useState<LoginMode>('team');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length < 6) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'admin') await loginAsAdmin(code);
      else await loginWithTeamCode(code);
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  };

  return (
    <main className="relative min-h-[100svh] overflow-x-hidden bg-[#FFFAF5] text-[#172033]" dir="rtl">
      <section className="relative min-h-[520px] overflow-hidden bg-[#172033] text-white lg:h-[70svh] lg:min-h-[500px]">
        <div className="arena-grid data-noise absolute inset-0 opacity-70" />
        <div className="dbx-dots absolute -left-20 top-14 h-[420px] w-[420px] rotate-12 opacity-20 [mask-image:radial-gradient(circle,black,transparent_72%)]" />
        <div className="absolute right-[-10%] top-[10%] size-[520px] rounded-full bg-brand/20 blur-[120px]" />
        <div className="absolute left-[18%] top-[12%] size-[420px] rounded-full bg-cyan/10 blur-[110px]" />

        <header className="relative z-10 mx-auto flex h-20 max-w-[1440px] items-center justify-between border-b border-white/8 px-8">
          <BrandLockup inverse />
        </header>

        <div className="relative z-10 mx-auto grid min-h-[440px] max-w-[1440px] items-center gap-4 px-5 pb-5 sm:px-8 lg:h-[calc(70svh-5rem)] lg:min-h-[420px] lg:grid-cols-[0.92fr_1.08fr]" dir="ltr">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative hidden h-full items-center justify-center lg:flex"
          >
            <DatabinexScene className="translate-y-[-26px] scale-[.98] 2xl:scale-[1.08]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.68, ease: [0.2, 0.8, 0.2, 1] }}
            className="justify-self-end text-right"
            dir="rtl"
          >
            <span className="mb-4 inline-flex items-center gap-2 rounded-xl bg-white/[.07] px-5 py-2.5 text-sm font-black text-white/82 ring-1 ring-white/10">
              <i className="size-2 rounded-full bg-brand shadow-[0_0_18px_rgba(242,106,33,.8)]" />
              رقابت زنده
            </span>
            <h1 className="text-4xl font-black leading-[1.12] tracking-tight lg:text-5xl 2xl:text-7xl">
              مسابقه دیتابینکس <span dir="ltr" className="inline-block whitespace-nowrap text-brand">T-SQL</span>
            </h1>
            <p className="mt-4 max-w-2xl text-xl font-bold leading-9 text-white/86 2xl:text-2xl 2xl:leading-10">
              به چالش بکشید، تحلیل کنید، پیروز شوید.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm font-medium text-white/56">
              <span className="inline-flex items-center gap-2"><Radio className="size-5" /> آنلاین و زنده</span>
              <span className="h-5 w-px bg-white/14" />
              <span className="inline-flex items-center gap-2"><CalendarDays className="size-5" /> مسیر {formatFaNumber(TEAM_QUESTION_COUNT)} سؤالی</span>
              <span className="h-5 w-px bg-white/14" />
              <span className="inline-flex items-center gap-2"><Users className="size-5" /> تیم‌های ۱ تا ۳ نفره</span>
            </div>

            <form onSubmit={(event) => void handleLogin(event)} className="mt-5 max-w-[560px] rounded-[22px] border border-white/12 bg-white/[.08] p-4 shadow-[0_24px_80px_rgba(0,0,0,.22)] backdrop-blur-xl">
              <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-[#111827]/55 p-1">
                <button type="button" onClick={() => setMode('team')} className={`flex h-12 items-center justify-center gap-2 rounded-xl text-sm font-black transition ${mode === 'team' ? 'bg-brand text-white shadow-[0_14px_36px_rgba(242,106,33,.34)]' : 'text-white/58 hover:text-white'}`}>
                  <KeyRound className="size-4" />
                  ورود تیم
                </button>
                <button type="button" onClick={() => setMode('admin')} className={`flex h-12 items-center justify-center gap-2 rounded-xl text-sm font-black transition ${mode === 'admin' ? 'bg-white text-[#172033]' : 'text-white/58 hover:text-white'}`}>
                  <UserCog className="size-4" />
                  ورود مدیر
                </button>
              </div>
              <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_178px]">
                <Input
                  dir="ltr"
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  placeholder={mode === 'admin' ? 'ADMIN-CODE' : 'TEAM-CODE'}
                  className="h-14 rounded-2xl border-white/10 bg-white text-center text-lg font-black tracking-[0.18em] text-[#172033] shadow-none"
                  autoComplete="one-time-code"
                />
                <Button size="lg" className="h-14 rounded-2xl bg-brand text-white shadow-[0_16px_42px_rgba(242,106,33,.34)] hover:bg-brand-dark" disabled={busy || code.trim().length < 6} type="submit">
                  {busy ? 'در حال بررسی…' : mode === 'admin' ? 'اتاق کنترل' : 'ثبت‌نام تیم'}
                  <ArrowLeft className="size-4" />
                </Button>
              </div>
              {error && <p role="alert" className="mt-3 rounded-xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm text-red-100">{error}</p>}
            </form>
            <a
              href="/leaderboard"
              className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-white/12 bg-white/[.06] px-5 py-3 text-sm font-black text-white/76 transition hover:border-brand/40 hover:bg-brand/15 hover:text-white"
            >
              مشاهده تابلوی زنده عمومی
              <Radio className="size-4 text-brand" />
            </a>
          </motion.div>
        </div>
      </section>

      <section className="relative z-20 min-h-[220px] border-t border-[#FFE2CE] bg-[#FFFAF5] lg:h-[30svh]">
        <div className="mx-auto grid h-full max-w-[1440px] grid-cols-1 items-start gap-5 px-5 py-4 sm:px-8 xl:grid-cols-[1.05fr_0.95fr] xl:gap-8">
          <div>
            <h2 className="flex items-center gap-3 text-2xl font-black text-[#172033]">
              <span className="h-8 w-1.5 rounded-full bg-brand" />
              درباره مسابقه
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#4A5565]">
              DataBinex Arena یک رقابت عملی برای سنجش مهارت تحلیل داده و نوشتن Query است. هر تیم با کد اختصاصی وارد می‌شود، سؤال‌های پایگاه‌داده‌ای را پاسخ می‌دهد و امتیاز نهایی از ترکیب دقت Query و زمان پاسخ محاسبه می‌شود.
            </p>
          </div>
          <div className="rounded-[18px] border border-[#FFE2CE] bg-white p-4 shadow-[0_18px_50px_rgba(95,55,32,.07)]">
            <div className="grid grid-cols-2 gap-y-5 divide-x-0 divide-[#FFE2CE] text-center sm:grid-cols-4 sm:divide-x sm:divide-x-reverse">
              <InfoBadge icon={<KeyRound />} title="ورود با کد" detail="برای هر تیم" />
              <InfoBadge icon={<BarChart3 />} title="امتیاز ترکیبی" detail="دقت Query و زمان" />
              <InfoBadge icon={<CheckCircle2 />} title="چالش واقعی" detail="بر اساس AdventureWorks" />
              <InfoBadge icon={<Trophy />} title="رقابت زنده" detail="با لیدربورد لحظه‌ای" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function InfoBadge({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return (
    <div className="px-4">
      <span className="mx-auto grid size-11 place-items-center rounded-2xl bg-brand-pale text-brand">{icon}</span>
      <b className="mt-3 block text-sm font-black text-[#172033]">{title}</b>
      <span className="mt-1 block text-xs leading-5 text-[#6F6A67]">{detail}</span>
    </div>
  );
}
